import React from "react";
import { IconButton, Tooltip } from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import ArrowRightAltIcon from "@material-ui/icons/ArrowRightAlt";
import SentimentPill from "../common/SentimentPill/SentimentPill";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import moment from "moment-timezone";

/**
 * Format expiration date from "2025-11-21" to "11/21"
 * Uses UTC to match the payload dates exactly
 */
const formatExpirationDate = (dateString) => {
  if (!dateString) return "";
  const date = moment.utc(dateString);
  const month = date.month() + 1; // moment months are 0-11
  const day = date.date();
  return `${month}/${day}`;
};

/**
 * Format contract string from API data
 */
const formatContract = (ticker, strikePrice, optionType, expirationDate) => {
  const formattedExpiration = formatExpirationDate(expirationDate);
  return `${ticker} $${strikePrice} ${optionType} ${formattedExpiration}`;
};

/**
 * Format expires in string
 */
const formatExpiresIn = (daysToExpiration) => {
  return `Expires in ${daysToExpiration}d`;
};

/**
 * Format price to currency string
 */
const formatPrice = (price) => {
  if (typeof price === "number") {
    return `$${price.toFixed(2)}`;
  }
  return price;
};

/**
 * Shared column definitions for AI Alerts tables (Live & Past)
 * @param {Function} handleCopyToClipboard - Callback for copying contract symbol
 * @param {Function} handleSeeDetails - Callback for "See details" action
 * @returns {Array} Column definitions for TradeAlgoTable
 */

const getAILiveAlertsColumns = (handleCopyToClipboard, handleSeeDetails) => [
  {
    field: "contract",
    headerName: "Contract",
    width: 280,
    minWidth: 280,
    renderCell: (params) => {
      // Support both old format (with contract/expiresIn) and new API format
      const contract =
        params.row.contract ||
        formatContract(
          params.row.ticker,
          params.row.strikePrice,
          params.row.optionType,
          params.row.expirationDate,
        );
      const expiresIn =
        params.row.expiresIn || formatExpiresIn(params.row.daysToExpiration);

      return (
        <div className="ai-alerts-contract-cell">
          <span className="contract-name">{contract}</span>
          <span className="contract-expires">{expiresIn}</span>
        </div>
      );
    },
  },
  {
    field: "sentiment",
    headerName: "Sentiment",
    width: 110,
    minWidth: 110,
    renderCell: (params) => <SentimentPill sentiment={params.value} />,
  },
  {
    field: "strategy",
    headerName: "Strategy",
    flex: 1,
    minWidth: 120,
    renderCell: (params) => (
      <div className="ai-alerts-strategy-chip">
        <span className="strategy-text">{params.value}</span>
      </div>
    ),
  },
  {
    field: "aiBuyPrice",
    headerName: "AI Buy Price",
    width: 110,
    maxWidth: 110,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      // Format price if it's a number, otherwise use as-is
      const formattedPrice =
        typeof params.value === "number"
          ? formatPrice(params.value)
          : params.value || formatPrice(params.row.aiBuyPrice);
      return (
        <div className="ai-alerts-price-cell">
          <span>{formattedPrice}</span>
        </div>
      );
    },
  },
  {
    field: "stopLoss",
    headerName: "Stop Loss",
    width: 100,
    maxWidth: 100,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const stopLoss = params.row.stopLoss;
      const formattedValue =
        stopLoss !== null && stopLoss !== undefined
          ? typeof stopLoss === "number"
            ? formatPrice(stopLoss)
            : stopLoss
          : "N/A";
      return (
        <div className="ai-alerts-price-cell">
          <span>{formattedValue}</span>
        </div>
      );
    },
  },
  {
    field: "takeProfit",
    headerName: "Take Profit",
    width: 100,
    maxWidth: 100,
    align: "right",
    headerAlign: "right",
    renderCell: (params) => {
      const takeProfit = params.row.takeProfit;
      const formattedValue =
        takeProfit !== null && takeProfit !== undefined
          ? typeof takeProfit === "number"
            ? formatPrice(takeProfit)
            : takeProfit
          : "N/A";
      return (
        <div className="ai-alerts-price-cell">
          <span>{formattedValue}</span>
        </div>
      );
    },
  },
  {
    field: "contractSymbol",
    headerName: "Contract Symbol",
    width: 160,
    minWidth: 160,
    renderCell: (params) => (
      <div className="ai-alerts-symbol-cell">
        <span className="symbol-text">{params.value}</span>
        <IconButton
          size="small"
          className="copy-icon-button"
          onClick={() => handleCopyToClipboard(params.value)}
        >
          <FileCopyOutlinedIcon style={{ fontSize: 16 }} />
        </IconButton>
      </div>
    ),
  },
  {
    field: "action",
    headerName: "",
    width: 146,
    sortable: false,
    renderCell: (params) => (
      <div
        className="ai-alerts-action-cell"
        onClick={() => handleSeeDetails(params.row)}
      >
        <span className="action-text">See details</span>
        <ArrowRightAltIcon className="action-icon" />
      </div>
    ),
  },
];

const getAIPastAlertsColumns = (handleCopyToClipboard, handleSeeDetails) => [
  {
    field: "contract",
    headerName: "Contract",
    width: 300,
    minWidth: 300,
    renderCell: (params) => (
      <div className="ai-alerts-contract-cell">
        <span className="contract-name">{params.row.contract}</span>
      </div>
    ),
  },
  {
    field: "strategy",
    headerName: "Strategy",
    flex: 1,
    minWidth: 120,
    renderCell: (params) => (
      <div className="ai-alerts-strategy-chip">
        <span className="strategy-text">{params.value}</span>
      </div>
    ),
  },
  {
    field: "aiBuyPrice",
    headerName: "AI Buy Price",
    width: 160,
    maxWidth: 160,
    renderHeader: () => (
      <div className="ai-alerts-past-price-header-cell">AI Buy Price</div>
    ),
    renderCell: (params) => (
      <div className="ai-alerts-past-price-cell">
        <span>{params.value || "N/A"}</span>
        {params.row.aiBuyDate ? ` on ${params.row.aiBuyDate}` : ""}
      </div>
    ),
  },
  {
    field: "aiExitPrice",
    headerName: "AI Exit Price",
    width: 160,
    maxWidth: 160,
    renderHeader: () => (
      <div className="ai-alerts-past-price-header-cell">
        <Tooltip
          arrow
          placement="top"
          title={
            <div className="custom-AI-tooltip">
              <div className="custom-AI-tooltip-content">
                Trade P&L and AI Exit Price are taken from the nearest hourly
                data point at the time of trade closure.
              </div>
            </div>
          }
        >
          <InfoOutlinedIcon className="stat-info-icon" />
        </Tooltip>
        AI Exit Price
      </div>
    ),
    renderCell: (params) => (
      <div className="ai-alerts-past-price-cell">
        <span>{params.value || "N/A"}</span>
        {params.row.aiExitDate ? ` on ${params.row.aiExitDate}` : ""}
      </div>
    ),
  },
  {
    field: "pl",
    headerName: "P&L",
    width: 160,
    maxWidth: 160,
    renderHeader: () => (
      <div className="ai-alerts-past-price-header-cell">
        <Tooltip
          arrow
          placement="top"
          title={
            <div className="custom-AI-tooltip">
              <div className="custom-AI-tooltip-content">
                Trade P&L and AI Exit Price are taken from the nearest hourly
                data point at the time of trade closure.
              </div>
            </div>
          }
        >
          <InfoOutlinedIcon className="stat-info-icon" />
        </Tooltip>
        P&L
      </div>
    ),
    renderCell: (params) => {
      const plValue =
        params.value !== null && params.value !== undefined ? params.value : 0;
      return (
        <div
          className={`ai-alerts-past-pl-cell ${plValue >= 0 ? "positive" : "negative"}`}
        >
          <span>
            {plValue >= 0 ? "+" : ""}
            {plValue}%
          </span>
        </div>
      );
    },
  },
];

export { getAILiveAlertsColumns, getAIPastAlertsColumns };
